import { FC, useEffect, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";

import { useAppDispatch } from "../../store/hooks";
import { fetchTags } from "../../store/tagStore/tagThunk";
import { ITag, TagAppliesTo } from "../../utils/tag.types";

interface TagSelectProps {
  /** Currently selected tag ids. */
  value?: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  /** Tag ids carried by the invoice's line items (from each SKU's Tag master selection). */
  suggestedTagIds?: string[];
}

/**
 * Invoice tag picker. Offers active tags from the account's Tag master whose
 * `appliesTo` includes INVOICE (the API also returns generic tags). Options are
 * kept in local state so this does not disturb the tags slice used by the
 * Tags master screen in Settings.
 */
const TagSelect: FC<TagSelectProps> = ({ value, onChange, disabled, required, suggestedTagIds }) => {
  const dispatch = useAppDispatch();

  const [options, setOptions] = useState<ITag[]>([]);
  // Last id this component auto-applied, so a tag the user picked is never overwritten.
  const autoAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const data: any = await dispatch(
          fetchTags(`appliesTo=${TagAppliesTo.INVOICE}&status=active`)
        ).unwrap();

        setOptions(data || []);
      } catch (error) {
        console.error('Error fetching invoice tags:', error);
      }
    };

    loadTags();
  }, [dispatch]);

  // `value` holds ids; map the first id back onto the loaded options for single-select display.
  const selectedId = Array.isArray(value) && value.length > 0 ? value[0] : null;
  const selected = options.find((option) => option._id === selectedId) || null;

  // Line-item tags that also apply to invoices. One distinct tag => auto-select it;
  // more than one (items tagged differently) => leave the pick to the user.
  const suggested = Array.from(new Set(suggestedTagIds || [])).filter((id) => options.some((o) => o._id === id));
  const suggestedKey = suggested.join(',');
  const conflicting = suggested.length > 1;

  useEffect(() => {
    if (options.length === 0) return;
    const next = suggested.length === 1 ? suggested[0] : null;
    if (next === autoAppliedRef.current) return;
    if (selectedId && selectedId !== autoAppliedRef.current) return;
    autoAppliedRef.current = next;
    onChange(next ? [next] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedKey, options]);

  return (
    <>
      <h5 className='c-label'>{required ? 'Tags *' : 'Tags'}</h5>
      <Autocomplete
        size='small'
        disabled={disabled}
        options={options}
        value={selected}
        onChange={(event, newValue: ITag | null) => onChange(newValue ? [newValue._id] : [])}
        getOptionLabel={(option: ITag) => option.name}
        isOptionEqualToValue={(option: ITag, val: ITag) => option._id === val._id}
        renderOption={(props, option: ITag) => (
          <li {...props} key={option._id} style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: option.color || '#7f8c9b',
              display: 'inline-block', flexShrink: 0
            }} />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className='c-text-field'
            variant="standard"
            placeholder={selected ? '' : 'Select Tag'}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <>
                  {selected && (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: selected.color || '#7f8c9b',
                        display: 'inline-block',
                        marginRight: 6,
                        marginLeft: 4,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ width: '100%', marginBottom: '3px' }}
      />
      {required && conflicting && !selected && (
        <span style={{ fontSize: 12, color: '#d32f2f' }}>Items have different tags — please select one.</span>
      )}
    </>
  );
};

export default TagSelect;
